import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/axios';
import { TrendingUp, Pause, Play, RefreshCw, DollarSign, Eye, MousePointer, Users } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { formatNumber, formatCurrency } from '@/utils/formatters';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import type { AdCampaign, AdPlatform } from '@/types';

export function AdsPage() {
  const [platform, setPlatform] = useState<'all' | AdPlatform>('all');
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ads', 'campaigns', platform],
    queryFn: async () => {
      const { data } = await apiClient.get<{ data: { campaigns: AdCampaign[] } }>('/ads/campaigns', {
        params: platform !== 'all' ? { platform } : {},
      });
      return data.data.campaigns;
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'active' | 'paused' }) => {
      await apiClient.patch(`/ads/campaigns/${id}/status`, { status });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ads', 'campaigns'] });
      toast.success('Campaign status updated');
    },
  });

  const syncCampaigns = useMutation({
    mutationFn: async (p: string) => {
      await apiClient.post(`/ads/sync/${p}/default`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['ads', 'campaigns'] });
      toast.success('Campaigns synced');
    },
  });

  const totalSpend = data?.reduce((sum, c) => sum + parseFloat(c.spend ?? '0'), 0) ?? 0;
  const totalImpressions = data?.reduce((sum, c) => sum + (c.impressions ?? 0), 0) ?? 0;
  const totalClicks = data?.reduce((sum, c) => sum + (c.clicks ?? 0), 0) ?? 0;
  const totalLeads = data?.reduce((sum, c) => sum + (c.leads ?? 0), 0) ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ads Manager</h1>
          <p className="text-slate-500 text-sm mt-0.5">Google & Meta campaign performance</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" icon={<RefreshCw size={14} />} onClick={() => refetch()}>Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => syncCampaigns.mutate('google')} loading={syncCampaigns.isPending}>
            Sync Google
          </Button>
          <Button variant="outline" size="sm" onClick={() => syncCampaigns.mutate('meta')} loading={syncCampaigns.isPending}>
            Sync Meta
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Spend', value: formatCurrency(totalSpend), icon: <DollarSign size={20} />, color: 'bg-red-50 text-red-600' },
          { label: 'Impressions', value: formatNumber(totalImpressions), icon: <Eye size={20} />, color: 'bg-blue-50 text-blue-600' },
          { label: 'Clicks', value: formatNumber(totalClicks), icon: <MousePointer size={20} />, color: 'bg-green-50 text-green-600' },
          { label: 'Leads Generated', value: formatNumber(totalLeads), icon: <Users size={20} />, color: 'bg-purple-50 text-purple-600' },
        ].map(stat => (
          <div key={stat.label} className="bg-white rounded-xl shadow-card border border-slate-100 p-5">
            <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center mb-3', stat.color)}>
              {stat.icon}
            </div>
            <div className="text-xl font-bold text-slate-900">{stat.value}</div>
            <div className="text-xs text-slate-500 mt-0.5">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Platform Filter */}
      <div className="flex gap-2">
        {(['all', 'google', 'meta'] as const).map(p => (
          <button
            key={p}
            onClick={() => setPlatform(p)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize',
              platform === p ? 'bg-brand-navy text-white' : 'bg-white text-slate-600 border border-slate-200 hover:border-brand-cyan',
            )}
          >
            {p === 'all' ? 'All Platforms' : p === 'google' ? '🔍 Google Ads' : '📘 Meta Ads'}
          </button>
        ))}
      </div>

      {/* Campaigns Table */}
      <div className="bg-white rounded-xl shadow-card border border-slate-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Spinner /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {['Platform', 'Campaign', 'Status', 'Impressions', 'Clicks', 'Leads', 'Spend', 'CPL', 'Actions'].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider px-4 py-3">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {data?.map(campaign => (
                <tr key={campaign.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <span className={clsx(
                      'px-2 py-1 rounded text-xs font-medium',
                      campaign.platform === 'google' ? 'bg-blue-50 text-blue-700' : 'bg-indigo-50 text-indigo-700',
                    )}>
                      {campaign.platform === 'google' ? '🔍 Google' : '📘 Meta'}
                    </span>
                  </td>
                  <td className="px-4 py-3 max-w-48">
                    <div className="font-medium text-slate-900 truncate">{campaign.name}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={clsx(
                      'px-2 py-0.5 rounded-full text-xs font-medium',
                      campaign.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500',
                    )}>
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{formatNumber(campaign.impressions)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatNumber(campaign.clicks)}</td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{formatNumber(campaign.leads)}</td>
                  <td className="px-4 py-3 text-slate-600">{formatCurrency(parseFloat(campaign.spend))}</td>
                  <td className="px-4 py-3 text-slate-600">{campaign.cpl ? formatCurrency(parseFloat(campaign.cpl)) : '—'}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => updateStatus.mutate({ id: campaign.id, status: campaign.status === 'active' ? 'paused' : 'active' })}
                      className={clsx(
                        'p-1.5 rounded-lg transition-colors',
                        campaign.status === 'active'
                          ? 'text-amber-500 hover:bg-amber-50'
                          : 'text-green-600 hover:bg-green-50',
                      )}
                    >
                      {campaign.status === 'active' ? <Pause size={14} /> : <Play size={14} />}
                    </button>
                  </td>
                </tr>
              ))}
              {!data?.length && (
                <tr>
                  <td colSpan={9} className="py-16 text-center text-slate-400">
                    <TrendingUp size={32} className="mx-auto mb-2 opacity-30" />
                    No campaigns found. Connect Google/Meta accounts to sync.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
